import { Component, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { acceptLegalDocuments } from "@/api/legal";
import { staleLegalVersionsMessage } from "@/api/errors";
import { LegalAcceptanceDialog } from "@/components/legal/LegalAcceptanceDialog";
import { LEGAL_ACCEPTANCE_SIGNUP_DESCRIPTION_COPY } from "@fomofi/shared";
import {
  acceptHeldPrompt,
  heldPromptShows,
  leaveHeldPrompt,
  releaseOnArrival,
  signOutWatcher,
} from "@/lib/pendingConsent";
import { NEW_ACCOUNT_DESTINATION, SSO_CONSENT_SAVE_FAILED } from "@/lib/ssoLanding";

/** If the prompt itself throws, don't strand the account behind it (G42, mobile's rule). */
class PromptBoundary extends Component<{ onFail: () => void; children: ReactNode }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onFail();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * G42 (Paul, 2026-10-21): the "Review our terms" prompt a new Google/Apple account left
 * unanswered shows again on its next signed-in visit, in place of the page, until the
 * person picks Accept or Not now. See `lib/pendingConsent.ts` for the rule and the pages
 * it never covers. Used in place of `<Routes>`: while the prompt is owed, no page renders
 * behind it (so nothing redirects, opens its own dialog, or can be used), and once it is
 * answered the routes take over again at Pick your plan.
 */
export function PendingConsentRoutes({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const clerk = useClerk();
  const { pathname } = useLocation();
  // Answered: on its way to Pick your plan, still held until it gets there (D3).
  const [answered, setAnswered] = useState(false);

  // Sign-out clears that account's flag, once the session has ended.
  useEffect(() => clerk.addListener(signOutWatcher()), [clerk]);

  // An answered prompt lets go only once Pick your plan is the current route, and the flag
  // clears there, before paint.
  const arrived = answered && pathname === NEW_ACCOUNT_DESTINATION;
  useLayoutEffect(() => {
    if (arrived) releaseOnArrival({ userId, pathname, answered });
  }, [arrived, answered, pathname, userId]);

  const shows = heldPromptShows({
    answered,
    isLoaded: isLoaded === true,
    isSignedIn: isSignedIn === true,
    userId,
    pathname,
  });
  if (!shows) return <Routes>{children}</Routes>;
  return <PendingConsentPrompt onAnswered={() => setAnswered(true)} />;
}

function PendingConsentPrompt({ onAnswered }: { onAnswered: () => void }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      await acceptHeldPrompt({
        navigate,
        accept: async () => acceptLegalDocuments("web", (await getToken()) ?? undefined),
      });
      onAnswered();
    } catch (err: unknown) {
      setError(staleLegalVersionsMessage(err) ?? SSO_CONSENT_SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PromptBoundary
      onFail={() => {
        leaveHeldPrompt(navigate);
        onAnswered();
      }}
    >
      <LegalAcceptanceDialog
        open
        isSubmitting={saving}
        errorMessage={error}
        onAccept={() => void accept()}
        onDismiss={() => {
          leaveHeldPrompt(navigate);
          onAnswered();
        }}
        description={LEGAL_ACCEPTANCE_SIGNUP_DESCRIPTION_COPY}
        answerOnly
      />
    </PromptBoundary>
  );
}
